import {
  AppointmentDtoSchema,
  AppointmentSchema,
  fromAppointmentDtoToAppointment,
} from "@/features/appointments/types/appointmentTypes";
import {
  ClientDtoSchema,
  ClientSchema,
  fromClientDtoToClient,
} from "@/features/clients/types/clientTypes";
import {
  ServiceDtoSchema,
  ServiceSchema,
  fromServiceDtoToService,
} from "@/features/services/types/serviceTypes";
import { z } from "zod";

const SearchHasMoreSchema = z.object({
  clients: z.boolean(),
  appointments: z.boolean(),
  services: z.boolean(),
});

const AppointmentSearchResultSchema = AppointmentSchema.extend({
  client: ClientSchema.pick({
    id: true,
    firstName: true,
    lastName: true,
  }).nullable(),
  matchedServices: z.array(ServiceSchema.pick({ id: true, name: true })),
});

export const SearchResponseSchema = z.object({
  clients: z.array(ClientSchema),
  appointments: z.array(AppointmentSearchResultSchema),
  services: z.array(ServiceSchema),
  hasMore: SearchHasMoreSchema,
});

const AppointmentSearchResultDtoSchema = AppointmentDtoSchema.extend({
  client: ClientDtoSchema.pick({
    id: true,
    first_name: true,
    last_name: true,
  }).nullable(),
  matched_services: z.array(ServiceDtoSchema.pick({ id: true, name: true })),
});

const SearchResponseDtoSchema = z.object({
  clients: z.array(ClientDtoSchema),
  appointments: z.array(AppointmentSearchResultDtoSchema),
  services: z.array(ServiceDtoSchema),
  hasMore: SearchHasMoreSchema,
});

export type AppointmentSearchResultDto = z.infer<typeof AppointmentSearchResultDtoSchema>;
export type SearchResponseDto = z.infer<typeof SearchResponseDtoSchema>;

export type AppointmentSearchResult = z.infer<typeof AppointmentSearchResultSchema>;
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

export function fromAppointmentSearchResultDtoToAppointmentSearchResult(
  dto: AppointmentSearchResultDto
): AppointmentSearchResult {
  const resultDto = AppointmentSearchResultDtoSchema.parse(dto);
  const appointment = fromAppointmentDtoToAppointment(resultDto);

  return {
    id: appointment.id,
    revision: appointment.revision,
    createdAt: appointment.createdAt,
    modifiedAt: appointment.modifiedAt,
    businessId: appointment.businessId,
    locationId: appointment.locationId,
    clientId: appointment.clientId,
    startAt: appointment.startAt,
    endAt: appointment.endAt,
    status: appointment.status,
    source: appointment.source,
    price: appointment.price,
    depositAmount: appointment.depositAmount,
    billingCurrency: appointment.billingCurrency,
    billingLocked: appointment.billingLocked,
    notes: appointment.notes,
    confirmedAt: appointment.confirmedAt,
    cancelledAt: appointment.cancelledAt,
    createdBy: appointment.createdBy,
    services: appointment.services,
    forms: appointment.forms,
    formsCapturedAt: appointment.formsCapturedAt,
    client: resultDto.client
      ? {
          id: resultDto.client.id,
          firstName: resultDto.client.first_name,
          lastName: resultDto.client.last_name,
        }
      : null,
    matchedServices: resultDto.matched_services.map((service) => ({
      id: service.id,
      name: service.name,
    })),
  };
}

export function fromSearchResponseDtoToSearchResponse(
  dto: SearchResponseDto
): SearchResponse {
  const searchDto = SearchResponseDtoSchema.parse(dto);

  return {
    clients: searchDto.clients.map(fromClientDtoToClient),
    appointments: searchDto.appointments.map(fromAppointmentSearchResultDtoToAppointmentSearchResult),
    services: searchDto.services.map(fromServiceDtoToService),
    hasMore: {
      clients: searchDto.hasMore.clients,
      appointments: searchDto.hasMore.appointments,
      services: searchDto.hasMore.services,
    },
  };
}
